import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock, Sparkles } from "lucide-react";import { PageHeader } from "../components/PageHeader";
import { Stagger, StaggerItem } from "../components/Reveal";
import api from "../lib/api";

export default function Services() {
  const [categories, setCategories] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [services, setServices] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const concern = params.get("concern") || "all";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/concerns").then((r) => setConcerns(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const filtered = services.filter(
    (s) =>
      (active === "all" || s.category === active) &&
      (concern === "all" || (s.concerns || []).includes(concern))
  );

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  return (
    <div data-testid="services-page">
      <Seo title="Med Spa Services & Treatments in Clinton Township" description="Explore our full menu of medical aesthetic treatments in Clinton Township, MI — injectables, laser, RF microneedling, HydraFacial, body contouring and hair restoration." path="/services" />
      <PageHeader
        overline="Treatments"
        title="Services crafted for you"
        subtitle="From injectables to regenerative therapies — explore our full menu of expert-led aesthetic and wellness treatments."
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-2 mb-14" data-testid="services-treatment-count">
        <div className="flex items-center justify-center gap-4 md:gap-6">
          <span className="hidden sm:block h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-iri-mid/50" />
          <p className="font-heading text-xl md:text-3xl font-light italic text-ink text-center leading-snug">
            Over <span className="not-italic font-medium bg-gradient-to-r from-[#8EC5FC] to-[#E0C3FC] bg-clip-text text-transparent">12,500</span> treatments performed since 2024
          </p>
          <span className="hidden sm:block h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-iri-mid/50" />
        </div>
      </div>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* treatment finder banner */}
          <Link
            to="/quiz"
            data-testid="services-quiz-banner"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 iri-gradient rounded-2xl p-6 md:p-7 mb-10 hover:scale-[1.01] transition-transform"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-ink/70 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-lg md:text-xl font-medium text-ink">Not sure which treatment is right for you?</h2>
                <p className="text-ink/70 text-sm mt-1">Answer a few quick questions and get personalized recommendations in under a minute.</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 bg-ink text-white rounded-full px-6 py-3 text-sm font-medium whitespace-nowrap group-hover:gap-3 transition-all shrink-0">
              Take the treatment finder <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          {/* filters */}
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Browse by category</p>
            <div className="flex flex-wrap gap-2.5">
              <FilterChip label="All treatments" active={active === "all"} onClick={() => setFilter("category", "all")} testid="filter-all" />
              {categories.map((c) => (
                <FilterChip
                  key={c.slug}
                  label={c.name}
                  active={active === c.slug}
                  onClick={() => setFilter("category", c.slug)}
                  testid={`filter-${c.slug}`}
                />
              ))}
            </div>
          </div>

          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Browse by concern</p>
            <div className="flex flex-wrap gap-2.5">
              <FilterChip label="Any concern" active={concern === "all"} onClick={() => setFilter("concern", "all")} testid="concern-all" />
              {concerns.map((c) => (
                <FilterChip
                  key={c}
                  label={c}
                  active={concern === c}
                  onClick={() => setFilter("concern", c)}
                  testid={`concern-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                />
              ))}
            </div>
          </div>

          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((s) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden border border-slate-100 hover-lift h-full"
                >
                  <div className="img-zoom aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-heading text-lg font-medium text-ink">{s.name}</h3>
                      <span className="text-sm text-iri-mid font-medium whitespace-nowrap">{s.price}</span>
                    </div>
                    <p className="text-sm text-[#5C6664] mt-2 leading-relaxed">{s.short}</p>
                    {s.member_savings && (
                      <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-ink iri-gradient px-3 py-1 rounded-full" data-testid={`savings-badge-${s.slug}`}>
                        <Sparkles className="h-3 w-3" /> {s.member_savings}
                      </span>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs text-[#8A9492]">
                        <Clock className="h-3.5 w-3.5" /> {s.duration}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm text-ink group-hover:gap-2 transition-all">
                        Details <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          {filtered.length === 0 && (
            <p className="text-center text-[#8A9492] py-20">No treatments match these filters yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 rounded-full text-sm transition-all border ${
        active
          ? "iri-gradient text-ink border-transparent font-medium"
          : "bg-white text-[#5C6664] border-slate-200 hover:border-iri-mid"
      }`}
    >
      {label}
    </button>
  );
}
